import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiEnvelope } from '@/types';
import { getMachineId } from '@/lib/machine-fingerprint';
import { recordApiCall, normalizeApiPath } from '@/lib/feedback/apiRing';

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

// Token accessors — set by AuthProvider after login
let getIdToken: (() => string | null) | null = null;
let getActiveSiteId: (() => string | null) | null = null;
let refreshSession: (() => Promise<string>) | null = null;
let onAuthFailure: (() => void) | null = null;

export function setAuthAccessors(accessors: {
  getIdToken: () => string | null;
  getActiveSiteId: () => string | null;
  refreshSession: () => Promise<string>;
  onAuthFailure: () => void;
}) {
  getIdToken = accessors.getIdToken;
  getActiveSiteId = accessors.getActiveSiteId;
  refreshSession = accessors.refreshSession;
  onAuthFailure = accessors.onAuthFailure;
}

// Start times for the feedback API ring. Kept off the config/headers so nothing is sent.
const requestStarts = new WeakMap<object, number>();

// Records only method, path, status, duration and correlation id: never bodies or headers.
function recordForFeedback(
  config: InternalAxiosRequestConfig | undefined,
  response: { status?: number; headers?: unknown } | undefined,
): void {
  try {
    if (!config) return;
    const started = requestStarts.get(config);
    const headers = response?.headers as { get?: (n: string) => unknown } | Record<string, unknown> | undefined;
    const raw =
      typeof (headers as { get?: unknown } | undefined)?.get === 'function'
        ? (headers as { get: (n: string) => unknown }).get('x-correlation-id')
        : (headers as Record<string, unknown> | undefined)?.['x-correlation-id'];
    recordApiCall({
      method: (config.method ?? 'get').toUpperCase(),
      path: normalizeApiPath(config.url, config.baseURL),
      status: typeof response?.status === 'number' ? response.status : null,
      ms: started === undefined ? 0 : Math.round(performance.now() - started),
      correlationId: typeof raw === 'string' ? raw : null,
    });
  } catch {
    // Recording must never fail a request.
  }
}

const apiClient = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

// A hung or failing fingerprint computation must never stall or fail a request —
// the header is an audit convenience, so after this long the request goes without it.
const MACHINE_ID_TIMEOUT_MS = 2000;

async function getMachineIdOrNull(): Promise<string | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      getMachineId(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), MACHINE_ID_TIMEOUT_MS);
      }),
    ]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// Request interceptor: attach JWT + active site + browser timezone + workstation id
apiClient.interceptors.request.use(async (config) => {
  requestStarts.set(config, performance.now());
  const token = getIdToken?.();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const siteId = getActiveSiteId?.();
  if (siteId) {
    config.headers['X-Site-Id'] = siteId;
  }

  // Per spec §4.6 — currency-bag + counting-session endpoints need the
  // caller's IANA timezone to format shift-scoped queries correctly.
  // Sent on every request (cheap, ~20 bytes) so the calculator handler
  // + any future timezone-sensitive endpoint gets it without per-call wiring.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      config.headers['X-Client-Timezone'] = tz;
    }
  } catch {
    // Legacy browsers without Intl.DateTimeFormat — skip the header.
    // The backend falls back to a site default.
  }

  // IC-827 — the browser fingerprint, so write handlers can record which
  // workstation entered or approved a transaction. Replaces the per-route
  // `machineId` body field (machine-id-fingerprint ADR-10). Memoised by
  // getMachineId(), so after the first request this is a resolved promise.
  const machineId = await getMachineIdOrNull();
  if (machineId) {
    config.headers['X-Machine-Id'] = machineId;
  }

  return config;
});

// 401 retry queue — prevents concurrent refresh stampede
let isRefreshing = false;
let pendingRequests: Array<(token: string) => void> = [];

// Response interceptor: unwrap envelope + 401 retry + error propagation
apiClient.interceptors.response.use(
  (response) => {
    recordForFeedback(response.config, response);
    const envelope = response.data as ApiEnvelope<unknown>;
    if (envelope && typeof envelope.success === 'boolean') {
      response.data = envelope.data;
      if (envelope.success && envelope.pagination) {
        (response as unknown as Record<string, unknown>).pagination = envelope.pagination;
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableConfig | undefined;
    recordForFeedback(originalRequest, error.response);

    if (error.response?.status === 401 && originalRequest && !originalRequest._retried) {
      originalRequest._retried = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshSession?.() ?? '';
          pendingRequests.forEach((cb) => cb(newToken));
          pendingRequests = [];
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch {
          pendingRequests = [];
          onAuthFailure?.();
          throw error;
        } finally {
          isRefreshing = false;
        }
      }

      return new Promise((resolve) => {
        pendingRequests.push((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(apiClient(originalRequest));
        });
      });
    }

    // Propagate structured server error if present
    const serverError = (error.response?.data as Record<string, unknown>)?.error;
    if (serverError) {
      (error as unknown as Record<string, unknown>).serverError = serverError;
    }

    throw error;
  },
);

export function setBaseURL(url: string) {
  apiClient.defaults.baseURL = url;
}

export default apiClient;
